import React from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { CalendarIcon } from 'src/v3/core/icons'
import { FilterList as FilterIcon } from '@mui/icons-material'
import {
  useUserTimelineFilters,
  courseCategories,
} from '../hooks/use-user-timeline-filters'

const FILTER_COLORS = {
  categories: 'primary' as const,
  activityTypes: 'info' as const,
}

interface UserTimelineFilterPanelProps {
  onFiltersChange: (
    queryParams: ReturnType<typeof useUserTimelineFilters>['queryParams'],
  ) => void
}

export default function UserTimelineFilterPanel({
  onFiltersChange,
}: UserTimelineFilterPanelProps) {
  let {
    filters,
    queryParams,
    updateFilter,
    resetFilters,
    activityTypeOptions,
    shouldShowCEFilters,
    hasActiveFilters,
  } = useUserTimelineFilters()

  React.useEffect(() => {
    onFiltersChange(queryParams)
  }, [queryParams, onFiltersChange])

  let customStartDate = filters.customStartDate
    ? dayjs(filters.customStartDate)
    : null
  let customEndDate = filters.customEndDate
    ? dayjs(filters.customEndDate)
    : null

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="px-6 mb-6">
        <Box className="flex items-start gap-4 mb-4">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              sx={{ backgroundColor: 'background.paper' }}
              value={filters.timeRange}
              onChange={(e) => updateFilter('timeRange', e.target.value as any)}
              label="Time Range"
              startAdornment={<CalendarIcon className="mr-2" />}
            >
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="quarter">Last Quarter</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label="Start Date"
            value={
              filters.timeRange === 'custom'
                ? customStartDate
                : (() => {
                    let now = dayjs()
                    switch (filters.timeRange) {
                      case 'week':
                        return now.subtract(1, 'week')
                      case 'month':
                        return now.subtract(1, 'month')
                      case 'quarter':
                        return now.subtract(3, 'months')
                      case 'year':
                        return now.subtract(1, 'year')
                      default:
                        return now.subtract(1, 'month')
                    }
                  })()
            }
            onChange={(newValue) =>
              updateFilter('customStartDate', newValue?.toISOString() || null)
            }
            disabled={filters.timeRange !== 'custom'}
            slotProps={{
              textField: {
                sx: { minWidth: 160, backgroundColor: 'background.paper' },
              },
            }}
          />
          <DatePicker
            label="End Date"
            value={filters.timeRange === 'custom' ? customEndDate : dayjs()}
            onChange={(newValue) =>
              updateFilter('customEndDate', newValue?.toISOString() || null)
            }
            disabled={filters.timeRange !== 'custom'}
            maxDate={dayjs()}
            slotProps={{
              textField: {
                sx: { minWidth: 160, backgroundColor: 'background.paper' },
              },
            }}
          />
        </Box>

        <Card variant="outlined" className="p-4">
          <Box className="flex items-center justify-between mb-4">
            <Typography variant="subtitle2" className="flex items-center gap-2">
              <FilterIcon />
              Advanced Filters
            </Typography>
            <Box className="flex items-center gap-4">
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.showLicenseCredentialEvents}
                    onChange={(e) =>
                      updateFilter(
                        'showLicenseCredentialEvents',
                        e.target.checked,
                      )
                    }
                    size="small"
                  />
                }
                label="License/Credential Events"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                sx={{ minWidth: 'auto' }}
              >
                Reset Filters
              </Button>
            </Box>
          </Box>

          <Box
            className="flex flex-col gap-4 md:flex-row"
            sx={{ '& > *': { flex: { md: '0 1 33%' } } }}
          >
            <Box>
              <TextField
                fullWidth
                label="Search events"
                placeholder="Search by title..."
                value={filters.searchKeyword}
                onChange={(e) => updateFilter('searchKeyword', e.target.value)}
                size="medium"
              />
            </Box>

            <Box>
              <Autocomplete
                multiple
                options={activityTypeOptions}
                getOptionLabel={(option) => option.label}
                value={activityTypeOptions.filter((option) =>
                  filters.selectedActivityTypes.includes(option.value),
                )}
                onChange={(_, value) =>
                  updateFilter(
                    'selectedActivityTypes',
                    value.map((option) => option.value),
                  )
                }
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Activity Types"
                    placeholder="Select activity types..."
                    slotProps={{
                      ...params.slotProps,

                      htmlInput: {
                        ...params.slotProps.htmlInput,
                        'aria-label': 'Filter by activity types',
                      },
                    }}
                  />
                )}
                renderValue={() => null}
                slotProps={{
                  listbox: {
                    'aria-label': 'Activity type options',
                  } as any,
                }}
              />
              {filters.selectedActivityTypes.length > 0 && (
                <Box className="flex flex-wrap gap-2 mt-2">
                  {activityTypeOptions
                    .filter((option) =>
                      filters.selectedActivityTypes.includes(option.value),
                    )
                    .map((option) => (
                      <Chip
                        key={`activity-${option.value}`}
                        variant="outlined"
                        label={option.label}
                        size="medium"
                        color={FILTER_COLORS.activityTypes}
                        onDelete={() => {
                          let newActivityTypes =
                            filters.selectedActivityTypes.filter(
                              (value) => value !== option.value,
                            )
                          updateFilter(
                            'selectedActivityTypes',
                            newActivityTypes,
                          )
                        }}
                      />
                    ))}
                </Box>
              )}
            </Box>

            {shouldShowCEFilters && (
              <Box>
                <Autocomplete
                  multiple
                  options={courseCategories}
                  value={filters.selectedCategories}
                  onChange={(_, value) =>
                    updateFilter('selectedCategories', value)
                  }
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Categories"
                      placeholder="Select categories..."
                      slotProps={{
                        ...params.slotProps,

                        htmlInput: {
                          ...params.slotProps.htmlInput,
                          'aria-label': 'Filter by categories',
                        },
                      }}
                    />
                  )}
                  renderValue={() => null}
                  slotProps={{
                    listbox: {
                      'aria-label': 'Category options',
                    } as any,
                  }}
                />
                {filters.selectedCategories.length > 0 && (
                  <Box className="flex flex-wrap gap-2 mt-2">
                    {filters.selectedCategories.map((category, index) => (
                      <Chip
                        key={`category-${category}`}
                        variant="outlined"
                        label={category}
                        size="medium"
                        color={FILTER_COLORS.categories}
                        onDelete={() => {
                          let newCategories = filters.selectedCategories.filter(
                            (_, i) => i !== index,
                          )
                          updateFilter('selectedCategories', newCategories)
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </LocalizationProvider>
  )
}
